import { inferRouteObject } from 'react-router-typed-object';

import { generatePath } from '@/js/libs/router';
import { not, or, PrivateRoute, Roles } from '@/js/libs/use-auth';

import { ADOMIK_SEATS_ROLES } from 'src/access/adomikSeatsAccess.ts';
import type { Condition } from 'src/components/RoleBasedRedirector.tsx';
import RoleBasedRedirector from 'src/components/RoleBasedRedirector.tsx';
import ProgrammaticMapping from 'src/scenes/ProgrammaticMapping/index.ts';
import Status from 'src/scenes/Status/index.ts';
import { programmaticMappingRouteRoles } from './sectionRoles.ts';

const index = true;

const xandrProgrammaticConditions: Condition[] = [
  { roles: Roles.ADMIN, to: generatePath('/programmatic-mapping/xandr-programmatic/overview') },
  { roles: not(Roles.ADMIN), to: generatePath('/programmatic-mapping/xandr-programmatic/brands-mapping') },
];

const googleProgrammaticConditions: Condition[] = [
  { roles: Roles.ADMIN, to: generatePath('/programmatic-mapping/google-programmatic/websites-mapping') },
  { roles: not(Roles.ADMIN), to: generatePath('/programmatic-mapping/google-programmatic/brands-mapping') },
];

const adomikProgrammaticConditions: Condition[] = [
  { roles: Roles.ADMIN, to: generatePath('/programmatic-mapping/adomik-programmatic/websites-mapping') },
  { roles: not(Roles.ADMIN), to: generatePath('/programmatic-mapping/adomik-programmatic/brands-mapping') },
];

const programmaticMappingConditions: Condition[] = [{ roles: or(Roles.ADMIN, Roles.PAYOUT), to: './xandr-programmatic' }];

export const programmaticMappingRouteObject = inferRouteObject({
  path: 'programmatic-mapping',
  element: <PrivateRoute roles={programmaticMappingRouteRoles} noAccessComponent={Status.NotFound} />,
  children: [
    {
      Component: ProgrammaticMapping.Helmet,
      children: [
        {
          index,
          element: <RoleBasedRedirector conditions={programmaticMappingConditions} noAccessElement={<Status.NotFound />} />,
        },
        {
          path: 'xandr-programmatic',
          element: <PrivateRoute roles={or(Roles.ADMIN, Roles.PAYOUT)} noAccessComponent={Status.NotFound} />,
          children: [
            {
              Component: ProgrammaticMapping.XandrProgrammatic.Layout,
              children: [
                {
                  index,
                  element: <RoleBasedRedirector conditions={xandrProgrammaticConditions} noAccessElement={<Status.NotFound />} />,
                },
                {
                  element: <PrivateRoute roles={Roles.ADMIN} noAccessComponent={Status.NotFound} />,
                  children: [
                    { index, Component: ProgrammaticMapping.XandrProgrammatic.OverviewRedirect },
                    {
                      path: 'overview',
                      children: [
                        { index, Component: ProgrammaticMapping.XandrProgrammatic.OverviewRedirect },
                        { path: ':date', Component: ProgrammaticMapping.XandrProgrammatic.Overview },
                      ],
                    },
                    { path: 'websites-mapping', Component: ProgrammaticMapping.XandrProgrammatic.WebsitesMapping },
                    { path: 'deals', Component: ProgrammaticMapping.XandrProgrammatic.Deals },
                  ],
                },
                {
                  element: <PrivateRoute roles={Roles.PAYOUT} noAccessComponent={Status.NotFound} />,
                  children: [
                    { path: 'brands-mapping', Component: ProgrammaticMapping.XandrProgrammatic.BrandsMapping },
                    { path: 'buyer-mapping', Component: ProgrammaticMapping.XandrProgrammatic.BuyerMapping },
                  ],
                },
              ],
            },
          ],
        },
        {
          path: 'google-programmatic',
          element: <PrivateRoute roles={or(Roles.ADMIN, Roles.PAYOUT)} noAccessComponent={Status.NotFound} />,
          children: [
            {
              Component: ProgrammaticMapping.GoogleProgrammatic.Layout,
              children: [
                {
                  index,
                  element: <RoleBasedRedirector conditions={googleProgrammaticConditions} noAccessElement={<Status.NotFound />} />,
                },
                {
                  element: <PrivateRoute roles={Roles.ADMIN} noAccessComponent={Status.NotFound} />,
                  children: [{ path: 'websites-mapping', Component: ProgrammaticMapping.GoogleProgrammatic.WebsitesMapping }],
                },
                {
                  element: <PrivateRoute roles={Roles.PAYOUT} noAccessComponent={Status.NotFound} />,
                  children: [
                    { path: 'brands-mapping', Component: ProgrammaticMapping.GoogleProgrammatic.BrandsMapping },
                    { path: 'buyer-mapping', Component: ProgrammaticMapping.GoogleProgrammatic.BuyerMapping },
                  ],
                },
              ],
            },
          ],
        },
        {
          path: 'adomik-programmatic',
          element: <PrivateRoute roles={or(Roles.ADMIN, Roles.PAYOUT)} noAccessComponent={Status.NotFound} />,
          children: [
            {
              Component: ProgrammaticMapping.AdomikProgrammatic.Layout,
              children: [
                {
                  index,
                  element: <RoleBasedRedirector conditions={adomikProgrammaticConditions} noAccessElement={<Status.NotFound />} />,
                },
                {
                  element: <PrivateRoute roles={Roles.ADMIN} noAccessComponent={Status.NotFound} />,
                  children: [
                    { path: 'websites-mapping', Component: ProgrammaticMapping.AdomikProgrammatic.WebsiteMappings },
                    { path: 'deals-dbe', Component: ProgrammaticMapping.AdomikProgrammatic.Deals },
                  ],
                },
                {
                  element: <PrivateRoute roles={ADOMIK_SEATS_ROLES} noAccessComponent={Status.NotFound} />,
                  children: [{ path: 'seats', Component: ProgrammaticMapping.AdomikProgrammatic.Seats }],
                },
                {
                  element: <PrivateRoute roles={Roles.PAYOUT} noAccessComponent={Status.NotFound} />,
                  children: [
                    { path: 'brands-mapping', Component: ProgrammaticMapping.AdomikProgrammatic.BrandMappings },
                    { path: 'buyer-mapping', Component: ProgrammaticMapping.AdomikProgrammatic.BuyerMappings },
                    { path: 'ssp-mapping', Component: ProgrammaticMapping.AdomikProgrammatic.SSPMappings },
                  ],
                },
              ],
            },
          ],
        },
        {
          path: 'invalid-traffic-deductions',
          element: <PrivateRoute roles={Roles.ADMIN} noAccessComponent={Status.NotFound} />,
          children: [{ index, Component: ProgrammaticMapping.InvalidTrafficDeductions }],
        },
      ],
    },
  ],
});
